import { setIsAddTagTypeFormSubmitted } from "@/features/clientDataSlice";
import { TClientTags, TAddTagType } from "@/ts/common-types";
import { DEFAULT_TAG_TYPES } from "@/ts/constants";
import { showToast } from "@/ts/modals";
import { normalizeTagType, updateTagMapping } from "@/ts/utils";
import { Button, Form, Input, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filter, flatten, map, values } from "underscore";

interface IAddTagTypeProps {
    isAddTagTypeModalVisible: boolean;
    setIsTagTypeModalVisible: Dispatch<SetStateAction<boolean>>;
}

const { Option } = Select;

const AddTagTypeModal = ({
    isAddTagTypeModalVisible,
    setIsTagTypeModalVisible,
}: IAddTagTypeProps) => {
    const dispatch = useDispatch();
    const handleModalDisplayChange = () => {
        setIsTagTypeModalVisible((prev) => !prev);
    };
    const [modalForm] = Form.useForm();

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const tagTypeListSet = new Set(
        tagTypeList.map(
            ({ tag_type, record_type }) =>
                `${normalizeTagType(tag_type)}-${record_type}`
        )
    );

    const [loading, setLoading] = useState<boolean>(false);

    const onFinish = async (formValues: TAddTagType) => {
        const { tagType, recordTypes } = formValues;

        const permissibleRecordTypes = filter(
            recordTypes,
            (recordType) =>
                filter(tagTypeList, (type) => type.record_type === recordType)
                    .length < 10
        );

        if (!permissibleRecordTypes.length) {
            showToast("danger", "Cannot add more than 10 tag types");
        } else {
            setLoading(true);

            const defaultTagTypes = map(
                flatten(values(DEFAULT_TAG_TYPES)),
                (tt) => normalizeTagType(tt)
            );

            const filteredRecordTypes = permissibleRecordTypes.filter(
                (recordType) =>
                    !tagTypeListSet.has(
                        `${normalizeTagType(tagType)}-${recordType}`
                    )
            );

            if (!filteredRecordTypes.length) {
                showToast("danger", "Tag type already exists");
            } else if (defaultTagTypes.includes(normalizeTagType(tagType))) {
                showToast(
                    "danger",
                    "Tag type already exists as a default type"
                );
            } else {
                const response = await updateTagMapping(
                    {
                        tagType,
                        recordTypes: filteredRecordTypes,
                    },
                    "type-add"
                );

                if (response.status) {
                    dispatch(setIsAddTagTypeFormSubmitted(true));
                    setIsTagTypeModalVisible(false);
                }
            }
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Add Tag Type"
            centered
            open={isAddTagTypeModalVisible}
            onCancel={handleModalDisplayChange}
            footer={null}
        >
            <Form
                name="modalForm"
                layout="vertical"
                form={modalForm}
                style={{ maxWidth: 600 }}
                onFinish={onFinish}
            >
                <Form.Item
                    label="Tag Type"
                    name="tagType"
                    rules={[
                        {
                            required: true,
                            message: "Please input tag type name",
                        },
                    ]}
                >
                    <Input placeholder="Input tag type" className="border" />
                </Form.Item>
                <Form.Item
                    label="Record Type"
                    name="recordTypes"
                    rules={[
                        {
                            required: true,
                            message: "Please select record type",
                        },
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select record type"
                        allowClear
                    >
                        <Option value="campaign">Campaign</Option>
                        <Option value="product">Product</Option>
                        <Option value="global">Global</Option>
                    </Select>
                </Form.Item>
                <div className="d-flex justify-content-end">
                    <Button onClick={handleModalDisplayChange}>Cancel</Button>
                    <Form.Item className="ms-3">
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            Add
                        </Button>
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default AddTagTypeModal;
